import { QdsChoiceBase } from '../shared/choice-base.js';
import { rovingIndex } from '../shared/primitive-utils.js';

/**
 * Compact single or multiple selection with native buttons.
 * Native invalid reporting focuses the visible control while preserving labels and cancelable invalid events.
 * @element qds-segmented-control
 * @attr {string} label - Visible group label and accessible name.
 * @attr {string} aria-label - Explicit group name.
 * @attr {string} aria-labelledby - External label IDs; their text names the internal group.
 * @attr {string} value - Default/reset single value; current value uses the property.
 * @attr {boolean} multiple - Multiple pressed buttons instead of a radio group.
 * @attr {string} name - Native form field name.
 * @attr {string} form - Associated form ID.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Constraint/author invalid message.
 * @attr {boolean} disabled - Disable all choices and omit form submission.
 * @attr {boolean} readonly - Preserve selection while retaining focus/navigation.
 * @attr {boolean} required - At least one enabled, available option must be selected.
 * @attr {boolean} invalid - Author-supplied invalid state.
 * @attr {'sm'|'md'|'lg'} size - Visual size; minimum 44px targets.
 * @attr {'blur'|'input'|'submit'} validate-on - Error display timing; default blur.
 * @property {ReadonlyArray<{value: string; label: string; disabled?: boolean; group?: string; description?: string}>} options - Typed choices with nonempty string values and labels; disabled choices are skipped.
 * @property {string} value - Current first selection; silent programmatic assignment.
 * @property {string[]} values - Current selected values in option order, especially for multiple mode.
 * @property {string[]} defaultValues - Reset values; takes precedence over the value attribute.
 * @csspart label - Visible group label.
 * @csspart options - Named radio group or multiple-action group.
 * @csspart option - Native choice button.
 * @csspart hint - Helper text.
 * @csspart error - Validation message.
 * @fires input - User selection; detail { component, value, values, multiple }.
 * @fires change - User selection; detail { component, value, values, multiple }.
 * @fires {{component: HTMLElement; valid: boolean; validity: {valid: boolean; valueMissing: boolean; customError: boolean}; message: string}} qds-validity-change - Validation transition.
 */
export class QdsSegmentedControl extends QdsChoiceBase {
  protected get _choiceStyles(): string {
    return `
      [part=options] { display: flex; flex-wrap: wrap; gap: .25rem; }
      [part=option] { min-width: 44px; min-height: 44px; padding: .5rem .875rem; font: inherit; font-weight: 500;
        border: 1px solid var(--theme-border-secondary, #a0aabe); border-radius: var(--theme-radius-sm, 4px);
        color: var(--theme-text-primary, #333); background: var(--theme-background-primary, #fff); cursor: pointer; }
      [part=option][data-selected=true] { background: var(--theme-accent-primary, #445e89); color: var(--theme-text-on-accent-primary, #fff); border-color: var(--theme-accent-primary, #445e89); }
      [part=option]:focus { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; }
      [part=option]:disabled { opacity: .55; cursor: not-allowed; }
      :host([data-invalid]) [part=option] { border-color: var(--theme-error, #8b1f00); }
      :host([size=sm]) [part=option] { font-size: .8125rem; padding-inline: .625rem; }
      :host([size=lg]) [part=option] { min-height: 48px; font-size: 1rem; }
      @media (forced-colors: active) { [part=option] { border-color: ButtonText; } [part=option][data-selected=true] { background: Highlight; color: HighlightText; border-color: Highlight; } }
    `;
  }
  protected _syncChoices(): void {
    this._control!.setAttribute('role', this.multiple ? 'group' : 'radiogroup');
    if (this.multiple) { this._control!.removeAttribute('aria-required'); this._control!.removeAttribute('aria-readonly'); }
    else { this._control!.setAttribute('aria-required', String(this.required)); this._control!.setAttribute('aria-readonly', String(this.readonly)); }
    this._control!.setAttribute('aria-disabled', String(this.disabled));
    const buttons = this._buttons(), selected = new Set(this.values);
    const tabStop = buttons.find(button => selected.has(button.dataset.choice!) && !this._options.find(option => option.value === button.dataset.choice)!.disabled) || buttons.find(button => !this._options.find(option => option.value === button.dataset.choice)!.disabled);
    for (const [index, button] of buttons.entries()) {
      button.disabled = this.disabled || Boolean(this._options[index].disabled); button.dataset.selected = String(selected.has(button.dataset.choice!));
      button.setAttribute('role', this.multiple ? 'button' : 'radio');
      button.setAttribute(this.multiple ? 'aria-pressed' : 'aria-checked', button.dataset.selected);
      button.removeAttribute(this.multiple ? 'aria-checked' : 'aria-pressed');
      button.tabIndex = !button.disabled && (this.multiple || button === tabStop) ? 0 : -1;
    }
  }
  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || this.disabled) return;
    const next = ['ArrowRight', 'ArrowDown'].includes(event.key), previous = ['ArrowLeft', 'ArrowUp'].includes(event.key);
    if (!next && !previous && event.key !== 'Home' && event.key !== 'End') return;
    const buttons = this._buttons().filter(button => !button.disabled); if (!buttons.length) return;
    const current = buttons.indexOf(this.shadowRoot!.activeElement as HTMLButtonElement);
    const rtl = getComputedStyle(this).direction === 'rtl' && ['ArrowLeft', 'ArrowRight'].includes(event.key);
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : rovingIndex(current, buttons.length, (next ? 1 : -1) * (rtl ? -1 : 1));
    event.preventDefault(); const target = buttons[index];
    if (!this.multiple) { this._choose(target.dataset.choice!); for (const button of buttons) button.tabIndex = button === target ? 0 : -1; }
    target.focus();
  };
  protected override _bind(): void { this._control!.addEventListener('keydown', this._onKeyDown); }
  protected override _unbind(): void { this._control?.removeEventListener('keydown', this._onKeyDown); }
}
if (!customElements.get('qds-segmented-control')) customElements.define('qds-segmented-control', QdsSegmentedControl);
